import { Link } from 'react-router-dom';
import type { articles as articlesType } from '@/mocks/articles';

type Article = (typeof articlesType)[number];

export default function ArticleCard({
  article,
  variant = 'default',
}: {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}) {
  const categoryColorMap: Record<string, string> = {
    'qinggan-lianjie': 'primary',
    'xingjiankang-zhishi': 'secondary',
    'hunyin-jingying': 'accent',
    'liangxing-goutong': 'primary',
    'xinli-jiankang': 'secondary',
    'qinzi-jiaoyu': 'accent',
  };

  const colorName = categoryColorMap[article.category] || 'primary';

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
      >
        <div className="flex flex-col lg:flex-row">
          <div className="lg:w-2/5 h-56 lg:h-auto overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="lg:w-3/5 p-6 lg:p-8 flex flex-col justify-center">
            <span
              className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 w-fit whitespace-nowrap bg-${colorName}-100 text-${colorName}-700`}
            >
              {article.tags[0]}
            </span>
            <h3 className="text-xl font-heading font-semibold text-foreground-900 mb-3 group-hover:text-primary-600 transition-colors">
              {article.title}
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime} 分钟阅读
              </span>
              <span>{article.publishedAt}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
            {article.title}
          </h4>
          <p className="text-xs text-foreground-500">{article.publishedAt}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
    >
      <div className="h-48 overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-5">
        <span
          className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium mb-3 whitespace-nowrap bg-${colorName}-100 text-${colorName}-700`}
        >
          {article.tags[0]}
        </span>
        <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-3 text-xs text-foreground-500">
          <span>{article.author}</span>
          <span>·</span>
          <span>{article.readTime} 分钟</span>
        </div>
      </div>
    </Link>
  );
}